import { CheckCircle2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CardShell } from '../../components/ui/card.js';

/**
 * Moldura das telas públicas (login, cadastro, senha, verificação).
 *
 * Duas colunas em telas largas — a proposta de valor à esquerda, o cartão à direita — e
 * só o cartão no celular. Sem cena animada: o que vende aqui é a promessa, em três
 * linhas, e um formulário que não atrapalha.
 */
export function AuthLayout({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-sidebar p-10 lg:flex xl:p-14 border-r border-sidebar-border">
        <AuthScene />
        <BrandMark />
        <div className="relative max-w-md">
          <h1 className="m-0 text-3xl font-semibold tracking-tight">
            Infraestrutura de WhatsApp para o seu produto.
          </h1>
          <p className="mt-3 mb-6 text-base text-muted-foreground">
            Uma API, um console e um número conectado em minutos. Você constrói; a Talkium mantém a
            conexão de pé.
          </p>
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0 text-sm">
            {[
              'Instâncias por QR Code ou código de pareamento',
              'Envio, recebimento e webhooks com reentrega',
              'Cobrança por instância, sem fidelidade — 10 dias grátis',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <CheckCircle2 className="size-4 shrink-0 text-primary" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative m-0 text-xs text-subtle">© {new Date().getFullYear()} Talkium</p>
      </aside>

      <main className="flex flex-col items-center justify-center px-4 py-10">
        <div className="mb-6 lg:hidden">
          <BrandMark />
        </div>
        <CardShell className="w-full max-w-[420px] p-6 sm:p-8">
          <h2 className="m-0 text-xl font-semibold">{title}</h2>
          {description && <p className="mt-1 mb-0 text-sm text-muted-foreground">{description}</p>}
          <div className="mt-5">{children}</div>
        </CardShell>
        {footer && <div className="mt-4 text-center text-sm text-muted-foreground">{footer}</div>}
      </main>
    </div>
  );
}

/**
 * A cena de fundo das telas públicas: o produto no centro e, nas pontas, o que entra
 * (instâncias, mensagens, webhooks) e o que sai (as ferramentas que o cliente já usa).
 * É só um SVG decorativo com os tokens do tema; some no celular junto com a coluna.
 */
function AuthScene() {
  const left = ['Instâncias', 'Mensagens', 'Webhooks', 'Grupos'];
  const right = ['API REST', 'SDKs', 'MCP para IA', 'n8n e Make'];
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full text-primary opacity-[0.55]"
      viewBox="0 0 640 720"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern id="auth-grid" width="28" height="28" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" fill="currentColor" opacity="0.18" />
        </pattern>
        <radialGradient id="auth-glow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="640" height="720" fill="url(#auth-grid)" />
      <circle cx="320" cy="420" r="260" fill="url(#auth-glow)" />
      <g fill="none" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.5">
        {left.map((_, i) => (
          <path key={`l${i}`} d={`M 120 ${300 + i * 70} C 220 ${300 + i * 70}, 230 420, 320 420`} />
        ))}
        {right.map((_, i) => (
          <path key={`r${i}`} d={`M 520 ${300 + i * 70} C 420 ${300 + i * 70}, 410 420, 320 420`} />
        ))}
      </g>
      <g fontSize="12" fontFamily="inherit">
        {left.map((label, i) => (
          <g key={label} transform={`translate(28 ${288 + i * 70})`}>
            <rect width="104" height="26" rx="13" fill="currentColor" fillOpacity="0.12" />
            <text x="52" y="17" textAnchor="middle" fill="currentColor">
              {label}
            </text>
          </g>
        ))}
        {right.map((label, i) => (
          <g key={label} transform={`translate(508 ${288 + i * 70})`}>
            <rect width="104" height="26" rx="13" fill="currentColor" fillOpacity="0.12" />
            <text x="52" y="17" textAnchor="middle" fill="currentColor">
              {label}
            </text>
          </g>
        ))}
      </g>
      <g transform="translate(320 420)">
        <circle r="34" fill="currentColor" fillOpacity="0.18" />
        <circle r="22" fill="currentColor" />
        <text y="6" textAnchor="middle" fontSize="18" fontWeight="700" fill="var(--brand-contrast)">
          T
        </text>
      </g>
    </svg>
  );
}

function BrandMark() {
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-2.5 text-[15px] font-semibold text-foreground no-underline hover:no-underline"
    >
      <span
        aria-hidden="true"
        className="grid size-8 place-items-center rounded-md bg-primary text-sm font-bold text-primary-foreground"
      >
        T
      </span>
      Talkium
    </Link>
  );
}

/** O erro da API num formulário público: mensagem e, quando houver, a lista de problemas. */
export function AuthError({ error }: { error: unknown }) {
  if (!error) return null;
  const { message, details } = error as { message?: string; details?: { problems?: string[] } };
  return (
    <div
      role="alert"
      className="mb-3 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger"
    >
      <strong>{message ?? 'Algo deu errado.'}</strong>
      {details?.problems && (
        <ul className="mt-1 mb-0 pl-4">
          {details.problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
